import { ApolloError } from '@apollo/client'
import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_KIND, BUTTON_SIZE } from 'parkdepot-shared/components/Buttons/types'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import LoadingAnimation from '../../components/LoadingAnimation/LoadingAnimation.component'
import Notification from '../../components/Notification/Notification.component'
import VehicleCard from '../../components/VehicleCard/VehicleCard.component'
import { useGetUser } from '../../hooks/useAuth'
import { NotificationType } from '../../types/notification.enum'
import { Vehicle } from '../../types/vehicle.interface'
import mapVehiclesWithPlates from '../../utils/mapVehiclesWithPlates.utils'
import {
    AddVehicleWrapper,
    NoVehiclesWrapper,
    FullWrapper,
    Title,
    NoVehicleIcon,
    NoVehicleText,
    NoVehicleDescription,
    NewVehicleButtonOverride,
    VehicleAddIcon,
    LoadingContainer,
    Subtitle
} from './MyVehicle.style'
import NewVehicleDialog from '../../components/NewVehicleDialog/NewVehicleDialog.component'
import { ErrorList, useVehicle } from '../../hooks/useVehicle.hook'
import { getDomainExtension } from '@utils/getDomainExtension'
import { useSendGTMEvent } from '@hooks/useSendGTMEvent.hook'

const MyVehicle = () => {
    const { t } = useTranslation()
    const history = useHistory()
    const { user, loading } = useGetUser()
    const { sendButtonClickEvent } = useSendGTMEvent()

    const [vehicles, setVehicles] = useState<Vehicle[]>([])
    const [showNotification, setShowNotification] = useState<NotificationType | null>(null)
    const [notificationMessageKey, setNotificationMessageKey] = useState<string | null>(null)
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [showAddVehicle, setShowAddVehicle] = useState(false)
    const [vehicleToEdit, setVehicleToEdit] = useState<Vehicle | null>(null)

    const { removeVehicle } = useVehicle()

    useEffect(() => {
        if (user) {
            const { vehicles, license_plates } = user
            const existingVehicles = mapVehiclesWithPlates(vehicles, license_plates)
            if (vehicles && vehicles.length > 0) {
                setVehicles(existingVehicles || [])
            }
        }
    }, [user, history, t])

    useEffect(() => {
        // if plate in query params, prefil plate and open dialog
        const searchParams = new URLSearchParams(history.location.search)
        const plate = searchParams.get('plate')
        const country = searchParams.get('country')
        if (plate) {
            const domain = getDomainExtension()

            setVehicleToEdit({
                id: '',
                plate: plate.toUpperCase(),
                country: country || domain,
                vehicle_type: 'compact_car',
                name: t('profile.vehicle.firstNameVehicleName', { firstName: user?.first_name })
            })
            setShowAddVehicle(true)
        }
    }, [history])

    const onConfirmDelete = async (vehicle: Vehicle) => {
        const { id, plate_id } = vehicle
        if (!id || !plate_id) return

        setDeleteLoading(true)
        const result = await removeVehicle(id, plate_id)
        if (result.success) {
            setShowNotification(NotificationType.POSITIVE)
            setNotificationMessageKey('profile.successfulChanges')
            setVehicles(vehicles.filter((vehicle) => vehicle.id !== id))
        } else {
            const error = result.error as ApolloError
            setShowNotification(NotificationType.NEGATIVE)
            if (error.message.includes('400') === true) {
                setNotificationMessageKey('profile.vehicle.errorVehicleNotFound')
            } else if (error.message.includes('409')) {
                setNotificationMessageKey('profile.vehicle.errorVehicleHasSubscriptions')
            } else {
                setNotificationMessageKey('errors.errorOnSave')
            }
        }
        setShowAddVehicle(false)
        setDeleteLoading(false)
    }

    const onVehicleClick = (vehicle: Vehicle) => {
        setVehicleToEdit(vehicle)
        setShowAddVehicle(true)
    }

    const onNewVehicleClick = () => {
        setVehicleToEdit(null)
        setShowAddVehicle(true)
        sendButtonClickEvent('add-vehicle-btn')
    }

    const onCloseNotification = () => {
        setShowNotification(null)
        setNotificationMessageKey(null)
    }

    const onSavingError = (error: string) => {
        if (error === ErrorList.DUPLICATE_PLATE) {
            setShowAddVehicle(false)
            setNotificationMessageKey(t('profile.vehicle.plateAlreadyExists'))
            setShowNotification(NotificationType.NEGATIVE)
        } else {
            setNotificationMessageKey(t('errors.errorOnSave'))
            setShowNotification(NotificationType.NEGATIVE)
        }
    }

    return (
        <FullWrapper>
            <Title>{t('profile.vehicle.title')}</Title>
            <Subtitle>{t('profile.vehicle.myVehicleSubtitle')}</Subtitle>
            {loading && (
                <LoadingContainer>
                    <LoadingAnimation />
                </LoadingContainer>
            )}
            {!loading &&
                vehicles.length > 0 &&
                vehicles.map((vehicle) => (
                    <VehicleCard key={vehicle.id} vehicle={vehicle} onVehicleClick={onVehicleClick} />
                ))}
            {!loading && vehicles.length === 0 && (
                <NoVehiclesWrapper>
                    <NoVehicleIcon className="pkd-car-no-found" />
                    <NoVehicleText>{t('profile.vehicle.noVehicleFound')}</NoVehicleText>
                    <NoVehicleDescription>{t('profile.vehicle.noVehicleFoundDescription')}</NoVehicleDescription>
                </NoVehiclesWrapper>
            )}
            {!loading && (
                <AddVehicleWrapper>
                    <Button
                        id="add-vehicle-btn"
                        onClick={() => onNewVehicleClick()}
                        $size={BUTTON_SIZE.medium}
                        $kind={BUTTON_KIND.primary}
                        overrides={NewVehicleButtonOverride}
                    >
                        <VehicleAddIcon className="wemolo-car-add" />
                        {t('profile.vehicle.addNewVehicleBtn')}
                    </Button>
                </AddVehicleWrapper>
            )}
            {showNotification && notificationMessageKey && (
                <Notification type={showNotification} onClose={onCloseNotification}>
                    {t(notificationMessageKey)}
                </Notification>
            )}
            <NewVehicleDialog
                isOpen={showAddVehicle}
                vehicle={vehicleToEdit || {}}
                deleteLoading={deleteLoading}
                setIsOpen={setShowAddVehicle}
                onDeleteClick={onConfirmDelete}
                onError={onSavingError}
            />
        </FullWrapper>
    )
}

export default MyVehicle
